import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ShieldAlert, ShoppingBag, MessageSquare, Layers, Ticket, DollarSign, TrendingUp, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-[#252A34]/60">Loading…</div>;
  const c = data.counts || {};
  const f = data.financials || {};

  const cards = [
    { label: "Users", value: c.users, icon: Users, href: "/admin/entities/users" },
    { label: "Providers", value: c.providers, icon: Users, href: "/admin/entities/provider_profiles" },
    { label: "Pending provider reviews", value: c.pending_provider_reviews, icon: ShieldAlert, href: "/admin/entities/provider_profiles?status=pending_review", tone: c.pending_provider_reviews > 0 ? "amber" : "" },
    { label: "Pending service reviews", value: c.pending_service_reviews, icon: Layers, href: "/admin/entities/services?status=pending_review", tone: c.pending_service_reviews > 0 ? "amber" : "" },
    { label: "Active bookings", value: c.active_bookings, icon: ShoppingBag, href: "/admin/entities/bookings" },
    { label: "Active orders", value: c.active_orders, icon: ShoppingBag, href: "/admin/entities/orders" },
    { label: "Open requests", value: c.open_requests, icon: MessageSquare, href: "/admin/entities/project_requests" },
    { label: "Open disputes", value: c.open_disputes, icon: ShieldAlert, href: "/admin/disputes", tone: c.open_disputes > 0 ? "rose" : "" },
    { label: "Open tickets", value: c.open_tickets, icon: Ticket, href: "/admin/entities/support_tickets" },
    { label: "New users · 30d", value: c.new_users_30d, icon: TrendingUp },
    { label: "New providers · 30d", value: c.new_providers_30d, icon: TrendingUp },
    { label: "New bookings · 7d", value: c.new_bookings_7d, icon: TrendingUp },
  ];

  const money = (x) => `$${(x || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Marketplace overview</h1>
        <p className="text-[#252A34]/70 text-sm mt-1">Live counts from the database. Click a card to drill in.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 mb-8">
        <MetricCard label="GMV (all time)" value={money(f.gmv)} tone="brand" />
        <MetricCard label="Platform revenue" value={money(f.platform_revenue)} tone="brand" />
        <MetricCard label="Refunds" value={money(f.refunded)} tone={f.refunded > 0 ? "rose" : ""} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
        {cards.map((c) => <MetricCard key={c.label} {...c} />)}
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        <RecentPanel title="Recent signups" items={data.recent?.signups || []} render={(u) => <>
          <div className="font-medium truncate">{u.display_name || u.email}</div>
          <div className="text-xs text-[#252A34]/60">{u.email}</div>
        </>} />
        <RecentPanel title="Pending services" items={data.recent?.pending_services || []} render={(s) => <>
          <div className="font-medium truncate">{s.title}</div>
          <div className="text-xs text-[#252A34]/60">{s.provider_id}</div>
        </>} />
        <RecentPanel title="Open disputes" items={data.recent?.disputes || []} render={(d) => <>
          <div className="font-medium truncate">{d.subject}</div>
          <div className="text-xs text-[#252A34]/60">{d.kind} · reserved ${(d.reserved_amount || 0).toFixed(2)}</div>
        </>} />
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon = TrendingUp, href, tone }) {
  const toneClass = tone === "rose" ? "text-rose-700 bg-rose-50 border-rose-200" : tone === "amber" ? "text-amber-700 bg-amber-50 border-amber-200" : tone === "brand" ? "text-[#165a71] bg-[#165a71]/5 border-[#165a71]/20" : "text-[#071b22] bg-white border-slate-200";
  const inner = (
    <div className={`rounded-2xl border p-4 ${toneClass}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#252A34]/70">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-2 font-display text-2xl font-semibold">{value ?? "—"}</div>
    </div>
  );
  return href ? <Link to={href} className="block hover:-translate-y-0.5 transition-transform" data-testid={`admin-metric-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>{inner}</Link> : <div data-testid={`admin-metric-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>{inner}</div>;
}

function RecentPanel({ title, items, render }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="font-display font-semibold text-[#071b22] mb-3">{title}</div>
      {items.length === 0 ? <div className="text-sm text-[#252A34]/60">Nothing recent.</div> : (
        <ul className="space-y-3">
          {items.map((r, i) => (
            <li key={r.id || i} className="text-sm text-[#252A34]">{render(r)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
